/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 8/25/2023
 */

import React, { useState, useMemo, useEffect } from "react"
import { MaterialReactTable } from "material-react-table"
import { applicationColumns } from "./applicationColumns"
import api from "../../../../base/services/ResApi"
import { FetchErrorHandeling } from "../../../../base/errorHandeling/FetchErrorHandeling"
import { Box, IconButton, Tooltip } from "@mui/material"
import { Check, HighlightOff } from "@mui/icons-material"
import ConfirmModal from "../../../components/confirmRejectModals/ConfirmModal"
import RejectModal from "../../../components/confirmRejectModals/RejectModal"
import { RequestErrorHandeling } from "../../../../base/errorHandeling/RequestErrorHandeling"
import Alerts from "../../../../base/errorHandeling/Alerts"

const PendingAPKTable = ({
  orgCode,
  setPendingCount,
  updateData,
  setUpdateData,
  isSearch,
  setIsSearch,
  filterType,
  filterByVal,
}) => {
  const [appRowCount, setAppRowCount] = useState(0)
  const [approvedData, setApprovedData] = useState([])
  const [pendDataLoading, setPendDataLoading] = useState(false)
  const [rowsData, setRowsData] = useState([])
  const [isSubmit, setIsSubmit] = useState(false)
  const [tableState, setTableState] = useState()

  //for success messages
  const [isSuccess, setIsSuccess] = useState(false)
  const [successMessages, setSuccessMessages] = useState([])

  //for error handling
  const [iserror, setIserror] = useState(false)
  const [iserrorShow, setIserrorShow] = useState(false)
  const [isUnauhtError, setIsUnauhtError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  //table state
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  //modal States
  const [confirmModelOpen, setConfirmModelOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)

  const columns = useMemo(() => [
    ...applicationColumns(),
    { accessorKey: "approvedAction", header: "Approval action" },
  ], [])

  let apprvConfig = {
    headers: {
      Action: "V_ALL_NON_APPR_APP",
      "Is-Pagination": true,
      "Page-Number": pagination.pageIndex + 1,
      "Page-Size": pagination.pageSize,
      "Sort-Column": "version_code",
      "Sort-Order": "desc",
      Filter: filterType,
      Value: filterByVal,
    },
  }

  useEffect(() => {
    if (isSearch || updateData || pagination.pageIndex || pagination.pageSize) {
      if (filterType) {
        setPendDataLoading(true)
        api
          .get("/applications", apprvConfig)
          .then((res) => {
            setApprovedData(res.data?.dataList)
            setPendingCount(res.data?.total)
            setAppRowCount(res.data?.total)
            setPendDataLoading(false)
            setErrorMessages([])
            setIserror(false)
            setIsUnauhtError(false)
            setIsSearch(false)
            setUpdateData(false)
          })
          .catch((error) => {
            setIsSearch(false)
            setPendDataLoading(false)
            setUpdateData(false)
            FetchErrorHandeling(
              error,
              setIserror,
              setErrorMessages,
              setIserrorShow,
              setIsUnauhtError
            )
          })
      }
    }
  }, [
    isSearch,
    updateData,
    isUnauhtError,
    pagination.pageIndex,
    pagination.pageSize,
  ])

  //remove error message after some time
  useEffect(() => {
    if (iserror === true) {
      setTimeout(() => {
        setIserror(false)
        setErrorMessages([])
      }, 10000)
    }
  }, [iserror])

  //remove success message after some time
  useEffect(() => {
    if (isSuccess === true) {
      setTimeout(() => {
        setIsSuccess(false)
        setSuccessMessages([])
      }, 5000)
    }
  }, [isSuccess])

  const handleClearData = () => {
    setIsSubmit(false)
    setConfirmModelOpen(false)
    setRejectModalOpen(false)
  }

  let patchPendConfig = {
    headers: {
      "Content-Type": "application/json",
    },
  }

  const handleAccept = () => {
    setIsSubmit(true)
    const approvedIdList = rowsData?.map((row) => ({
      approvedId: row.approvedId,
    }))

    const pendUser = {
      approvedIdList,
      rejectedIdList: null,
    }
    const appRow = JSON.stringify(pendUser)

    api
      .patch("/applications", appRow, patchPendConfig)
      .then((res) => {
        tableState.resetRowSelection()
        setErrorMessages([])
        setIserror(false)
        setErrorMessages([])
        setIsUnauhtError(false)
        setIsSuccess(true)
        setSuccessMessages([res.data?.description])
        handleClearData()
        setUpdateData(true)
      })
      .catch((error) => {
        tableState.resetRowSelection()
        setIsSuccess(false)
        RequestErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
        setTimeout(() => {
          handleClearData()
        }, 1000)
      })
  }

  const handleReject = () => {
    setIsSubmit(true)
    const rejectedIdList = rowsData?.map((row) => ({
      rejectedId: row.approvedId,
    }))

    const pendUser = {
      approvedIdList: null,
      rejectedIdList,
    }
    const appRow = JSON.stringify(pendUser)

    api
      .patch("/applications", appRow, patchPendConfig)
      .then((res) => {
        tableState.resetRowSelection()
        setErrorMessages([])
        setIserror(false)
        setErrorMessages([])
        setIsUnauhtError(false)
        setIsSuccess(true)
        setSuccessMessages([res.data?.description])
        handleClearData()
        setUpdateData(true)
      })
      .catch((error) => {
        tableState.resetRowSelection()
        setIsSuccess(false)
        RequestErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
        setTimeout(() => {
          handleClearData()
        }, 1000)
      })
  }

  return (
    <div>
      <div>
        <Alerts
          iserror={iserror}
          errorMessages={errorMessages}
          isSuccess={isSuccess}
          successMessages={successMessages}
          iserrorShow={iserrorShow}
        />
      </div>
      <br />
      <MaterialReactTable
        columns={columns}
        data={approvedData ?? []}
        state={{
          columnFilters,
          globalFilter,
          pagination,
          showProgressBars: pendDataLoading,
        }}
        getRowId={(row) => String(row.applicationId)}
        initialState={{
          showColumnFilters: false,
          columnVisibility: {
            appId: false,
            versionCode: false,
            description: false,
            createdDateTime: false,
            appLogo: false,
          },
        }}
        manualPagination
        muiToolbarAlertBannerProps={
          iserror
            ? {
                color: "error",
                children: "Error loading data",
              }
            : undefined
        }
        onColumnFiltersChange={setColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        onPaginationChange={setPagination}
        rowCount={appRowCount}
        muiTablePaginationProps={{
          rowsPerPageOptions: [10],
        }}
        editingMode="modal"
        enableColumnOrdering
        enableGrouping
        enablePinning
        enableColumnResizing
        columnResizeMode="onEnd"
        enableRowSelection
        enableSelectAll
        renderTopToolbarCustomActions={({ table }) => {
          return (
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip arrow placement="left" title="Accept">
                  <IconButton
                    color="success"
                    disabled={!table.getSelectedRowModel().flatRows.length}
                    onClick={() => {
                      setRowsData([])
                      table.getSelectedRowModel().flatRows.forEach((row) => {
                        setRowsData((prevData) => [
                          ...prevData,
                          { ...row.original },
                        ])
                      })
                      setConfirmModelOpen(true)
                      setTableState(table)
                    }}
                    variant="contained"
                  >
                    <Check />
                  </IconButton>
                </Tooltip>
                <Tooltip arrow placement="right" title="Reject">
                  <IconButton
                    color="error"
                    disabled={!table.getSelectedRowModel().flatRows.length}
                    onClick={() => {
                      setRowsData([])
                      table.getSelectedRowModel().flatRows.forEach((row) => {
                        setRowsData((prevData) => [
                          ...prevData,
                          { ...row.original },
                        ])
                      })
                      setRejectModalOpen(true)
                      setTableState(table)
                    }}
                    variant="contained"
                  >
                    <HighlightOff />
                  </IconButton>
                </Tooltip>
              </Box>
            </div>
          )
        }}
      />
      <ConfirmModal
        open={confirmModelOpen}
        isSubmit={isSubmit}
        handleAccept={handleAccept}
        onClose={() => {
          setConfirmModelOpen(false)
        }}
      />
      <RejectModal
        open={rejectModalOpen}
        isSubmit={isSubmit}
        handleReject={handleReject}
        onClose={() => {
          setRejectModalOpen(false)
        }}
      />
    </div>
  )
}

export default PendingAPKTable
